import React, { useState } from 'react';

import Link from 'next/link';

import { motion, useScroll, useTransform } from 'framer-motion';
import millify from 'millify';

import ExportedImage from '@/components/shared/ExportedImage';
import GoldCheckmark from '@/components/shared/GoldCheckmark';
import GreenCheckmark from '@/components/shared/GreenCheckmark';
import RedXMark from '@/components/shared/RedXMark';
import WalletComparison from '@/components/shared/WalletComparison';
import { FAQ } from '@/components/shared/lp/developer/FAQ';
import { protocolAbbreviations } from '@/components/shared/lp/komodo-wallet/supported-assets/protocolAbbreviations';
import { handleIconError } from '@/components/shared/lp/komodo-wallet/supported-assets/utils/IconErrorHandler';

import { apiEndpoints } from '@/data/API_ENDPOINTS';
import { siteData } from '@/data/configs/site';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';
import assetSupportWide from '@/public/images/lp/wallet/asset-support-wide.png';
import atomicSwapExplained from '@/public/images/lp/wallet/atomic-swap-explained.png';
// Images from Figma design (Frame 9423:1882) - exported to wallet directory
import desktopMobile from '@/public/images/lp/wallet/desktop-mobile-combo.png';
import dexComparison from '@/public/images/lp/wallet/dex-comparison.png';
import guiInterface from '@/public/images/lp/wallet/gui-interface.png';
import iphoneMockup from '@/public/images/lp/wallet/iphone-mockup.png';
import block from '@/public/images/lp/wallet/kmd-block.png';
import macbookMockup from '@/public/images/lp/wallet/macbook-mockup.png';
import noLiquidityPools from '@/public/images/lp/wallet/no-liquidity-pools.png';
import protocolComparison from '@/public/images/lp/wallet/protocol-comparison.png';
import securityVisual from '@/public/images/lp/wallet/security-visual.png';
import timelineVisual from '@/public/images/lp/wallet/timeline-visual.png';
import walletFeaturesComparison from '@/public/images/lp/wallet/wallet-features-comparison.png';
import { fetchHandler } from '@/shared-functions/fetchHandler';
import { calculateTradingPairs } from '@/shared-functions/helpers.js';

const assetsListed = await fetchHandler(apiEndpoints.coins);
const tradeableAssetsList = assetsListed.filter((asset) => asset.wallet_only !== true);
const tradingPairs = calculateTradingPairs(tradeableAssetsList.length);

const stats = [
  { value: '10+', label: 'Supported Blockchain Protocols' },
  { value: '50+', label: 'Supported Blockchains' },
  { value: `${millify(tradingPairs)}`, label: 'Trading Pairs' },
  { value: `${parseInt(new Date().getFullYear() - 2016)}+`, label: 'Years of Active Development' },
  { value: '100%', label: 'Open-Source Codebase' },
];

const miscStats = { supportedCryptosCount: '500+' };

const timeline = [
  { year: '2012', text: 'Conceptual Idea' },
  { year: '2013', text: 'Protocol Formalized' },
  { year: '2014', text: 'First Working Implementation' },
  { year: '2016', text: 'Core Bitcoin Upgrade Enables Swaps' },
  { year: '2017', text: 'BarterDEX Launches' },
  { year: '2018', text: 'BTC ↔ ETH Swaps Achieved' },
  { year: '2022', text: 'Tech Hardened and Scaled' },
  { year: '2025', text: '3rd Gen UI & Core Protocol' },
];

const faqData = [
  {
    faqNo: '1',
    faqQstn: 'What makes Gleec Exchange Wallet different from other wallets?',
    faqAnswer:
      'Gleec Exchange Wallet is the only wallet that offers true peer-to-peer atomic swaps between native Bitcoin, UTXO coins, and EVM chains without wrapped tokens or middlemen. It combines a secure non-custodial wallet with a fully decentralized exchange.',
  },
  {
    faqNo: '2',
    faqQstn: 'Is Gleec Exchange Wallet safe to use?',
    faqAnswer:
      'Yes, Gleec Exchange Wallet is completely non-custodial. Your private keys never leave your device, and all code is 100% open source. The wallet has undergone thorough security assessments and uses industry-leading security practices.',
  },
  {
    faqNo: '3',
    faqQstn: 'Which platforms is Gleec Exchange Wallet available on?',
    faqAnswer:
      'Gleec Exchange Wallet is available on desktop (Windows, macOS, Linux), mobile (iOS and Android), and as a web application. You can access your funds from any platform using your seed phrase.',
  },
  {
    faqNo: '4',
    faqQstn: 'How do atomic swaps work?',
    faqAnswer:
      'Atomic swaps use smart contract technology (HTLCs) to enable trustless exchanges. Either both parties receive their funds, or the swap cancels and funds return to their original owners. No trust or third party is required.',
  },
  {
    faqNo: '5',
    faqQstn: 'Can I add custom tokens to Gleec Exchange Wallet?',
    faqAnswer:
      'Yes! You can easily add any ERC-20, BEP-20, or PLG20 token by simply pasting its contract address. The wallet automatically fetches token details and adds it to your interface.',
  },
];

const comparisonFeatures = [
  { feature: 'Native Bitcoin Support', komodo: true, others: false },
  { feature: 'True Cross-Chain Swaps', komodo: true, others: false },
  { feature: 'No Wrapped Tokens', komodo: true, others: false },
  { feature: '100% Open Source', komodo: true, others: true },
  { feature: 'No Middlemen', komodo: true, others: false },
  { feature: 'P2P Order Book', komodo: true, others: false },
  { feature: 'No Impermanent Loss', komodo: true, others: false },
  { feature: 'Self-Custody', komodo: true, others: true },
  { feature: 'Multi-Platform', komodo: true, others: true },
  { feature: 'Hardware Wallet Support', komodo: true, others: true },
];

export default function KomodoWalletPage() {
  const [selected, setSelected] = useState(null);
  const { scrollY } = useScroll();

  // Parallax effects for background gradients
  const heroLeftY = useTransform(scrollY, [0, 1000], [0, 80]);
  const heroRightX = useTransform(scrollY, [0, 1000], [0, 120]);
  const heroCenterY = useTransform(scrollY, [0, 1500], [0, -100]);

  const statsLeftX = useTransform(scrollY, [1000, 2500], [0, -100]);
  const statsRightX = useTransform(scrollY, [1000, 2500], [0, 150]);
  const statsCenterY = useTransform(scrollY, [1500, 3000], [0, -80]);

  const timelineLeftX = useTransform(scrollY, [3000, 5000], [0, -120]);
  const timelineRightX = useTransform(scrollY, [3500, 5500], [0, 150]);
  const timelineCenterY = useTransform(scrollY, [4000, 6000], [0, -100]);

  const featuresLeftX = useTransform(scrollY, [5500, 7500], [0, -100]);
  const featuresRightX = useTransform(scrollY, [5500, 7500], [0, 120]);
  const featuresCenterY = useTransform(scrollY, [6500, 8500], [0, -90]);

  const assetLeftX = useTransform(scrollY, [8000, 10000], [0, -110]);
  const assetRightX = useTransform(scrollY, [8500, 10500], [0, 130]);

  const faqLeftX = useTransform(scrollY, [11000, 13000], [0, -80]);
  const faqRightX = useTransform(scrollY, [11000, 13000], [0, 100]);

  // Gradient animations
  const gradientVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.8, transition: { duration: 1.2, ease: 'easeOut' } },
  };

  return (
    <>
      <main className='relative z-[100] font-sans text-white'>
        {/* Background Gradients - Positioned to match Figma design */}
        {/* Hero section lights - Top of page */}
        {/* Ellipse 1803 - Top left */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            y: heroLeftY,
          }}
          className='absolute -left-[550px] top-[216px] z-[-10] h-[326px] w-[648px] md:-left-[600px] md:top-[200px] md:h-[360px] md:w-[700px] lg:-left-[512px] lg:top-[216px] lg:h-[326px] lg:w-[648px] 4xl:-top-[220px] 4xl:left-1/2 4xl:-ml-[364px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 'some' }}
          variants={gradientVariants}
        />
        {/* Ellipse 1805 - Top right */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: heroRightX,
          }}
          className='absolute -right-[400px] top-[42px] z-[-10] h-[365px] w-[545px] md:-right-[450px] md:top-[50px] md:h-[380px] md:w-[580px] lg:-right-[396px] lg:top-[42px] lg:h-[365px] lg:w-[545px] 4xl:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 'some' }}
          variants={gradientVariants}
        />
        {/* Ellipse 1804 - Large hero center glow */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            y: heroCenterY,
          }}
          className='absolute left-[50px] top-[750px] z-[-10] h-[544px] w-[900px] md:left-[100px] md:top-[780px] md:h-[580px] md:w-[1000px] lg:left-[148px] lg:top-[811px] lg:h-[544px] lg:w-[1129px] xl:left-[48px] xl:top-[761px] 2xl:left-[148px] 2xl:top-[811px] 3xl:left-[348px] 3xl:top-[811px] 4xl:left-1/2 4xl:-ml-[612px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1807 - Left side near stats */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: statsLeftX,
          }}
          className='absolute -left-[150px] top-[1600px] z-[-10] h-[710px] w-[336px] md:-left-[170px] md:top-[1620px] md:h-[720px] md:w-[360px] lg:-left-[172px] lg:top-[1632px] lg:h-[710px] lg:w-[336px] 4xl:left-[50%] 4xl:top-[1380px] 4xl:-ml-[calc(672px)] 4xl:h-[310px] 4xl:w-[336px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1806 - Right side near stats */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: statsRightX,
          }}
          className='absolute right-[-150px] top-[1700px] z-[-10] h-[531px] w-[499px] md:right-[-200px] md:top-[1720px] md:h-[540px] md:w-[510px] lg:right-[-279px] lg:top-[1742px] lg:h-[531px] lg:w-[499px] 4xl:left-[50%] 4xl:top-[1380px] 4xl:ml-[calc(272px)] 4xl:h-[310px] 4xl:w-[336px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />

        {/* Stats section lights */}
        {/* Ellipse 1820 - Left */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: statsLeftX,
          }}
          className='absolute -left-[380px] top-[2600px] z-[-10] h-[372px] w-[520px] md:-left-[400px] md:top-[2620px] md:h-[390px] md:w-[530px] lg:-left-[244px] lg:top-[2639px] lg:h-[372px] lg:w-[520px] 4xl:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 'some' }}
          variants={gradientVariants}
        />
        {/* Ellipse 1821 - Right */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: statsRightX,
          }}
          className='absolute -right-[350px] top-[2600px] z-[-10] h-[372px] w-[520px] md:-right-[370px] md:top-[2620px] md:h-[390px] md:w-[530px] lg:-right-[194px] lg:top-[2639px] lg:h-[372px] lg:w-[520px] 4xl:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 'some' }}
          variants={gradientVariants}
        />
        {/* Ellipse 1822 - Center */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            y: statsCenterY,
          }}
          className='absolute left-1/2 top-[2680px] z-[-10] -ml-[200px] h-[172px] w-[320px] md:top-[2900px] lg:top-[3000px] xl:top-[2818px] 3xl:top-[2918px] -md:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />

        {/* Atomic Swap / Timeline section lights */}
        {/* Ellipse 1809 - Left side */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: timelineLeftX,
          }}
          className='absolute -left-[500px] top-[3250px] z-[-10] h-[800px] w-[687px] md:-left-[540px] md:top-[3280px] md:h-[810px] md:w-[690px] lg:-left-[551px] lg:top-[3308px] lg:h-[800px] lg:w-[687px] 4xl:left-1/2 4xl:top-[3600px] 4xl:-ml-[400px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1813 - Far right (overflow effect) */}
        {/* <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: timelineRightX,
          }}
          className='absolute -right-[800px] top-[4600px] z-[-10] h-[880px] w-[687px] md:-right-[850px] md:top-[4650px] md:h-[890px] md:w-[690px] lg:left-1/2 lg:top-[4895px] lg:-ml-[400px] lg:h-[880px] lg:w-[687px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />*/}
        {/* Ellipse 1815 - Center */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            y: timelineCenterY,
          }}
          className='absolute z-[-10] hidden md:left-[80px] md:top-[5180px] md:block md:h-[800px] md:w-[500px] lg:left-1/2 lg:top-[4900px] lg:-ml-[340px] lg:h-[473px] lg:w-[198px] xl:top-[4800px] xl:-ml-[510px] xl:h-[673px] xl:w-[298px] 2xl:top-[5000px] 2xl:-ml-[530px] 3xl:top-[5055px] 3xl:-ml-[550px] -lg:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />

        {/* Core Features section lights */}
        {/* Ellipse 1811 - Left */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: featuresLeftX,
          }}
          className='absolute -left-[480px] top-[6750px] z-[-10] h-[710px] w-[687px] md:-left-[500px] md:top-[6780px] md:h-[720px] md:w-[690px] lg:-left-[531px] lg:top-[6811px] lg:h-[710px] lg:w-[687px] 4xl:left-[50%] 4xl:top-[6900px] 4xl:-translate-x-[calc(50%+531px)]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1817 - Right */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: featuresRightX,
          }}
          className='absolute -right-[400px] top-[6600px] z-[-10] h-[710px] w-[765px] md:-right-[450px] md:top-[6640px] md:h-[730px] md:w-[780px] lg:-right-[498px] lg:top-[6680px] lg:h-[710px] lg:w-[765px] 4xl:hidden'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1814 - Center */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            y: featuresCenterY,
          }}
          className='absolute -right-[600px] top-[7600px] z-[-10] h-[773px] w-[742px] md:top-[7640px] md:h-[780px] md:w-[745px] lg:-right-[500px] lg:top-[7680px] lg:h-[773px] lg:w-[742px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1818 - Left security area */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: featuresLeftX,
          }}
          className='absolute -left-[300px] top-[7920px] z-[-10] h-[501px] w-[485px] md:-left-[320px] md:top-[7950px] md:h-[510px] md:w-[490px] lg:-left-[329px] lg:top-[7985px] lg:h-[501px] lg:w-[485px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />

        {/* Asset Support / Comparison section lights */}
        {/* Ellipse 1819 - Left */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: assetLeftX,
          }}
          className='absolute -left-[480px] top-[9050px] z-[-10] h-[710px] w-[1014px] md:-left-[500px] md:top-[9100px] md:h-[720px] md:w-[1030px] lg:-left-[515px] lg:top-[9145px] lg:h-[710px] lg:w-[1014px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1811 - Right (duplicate ellipse name) */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: assetRightX,
          }}
          className='absolute -right-[600px] top-[9680px] z-[-10] h-[710px] w-[1441px] md:-right-[700px] md:top-[9720px] md:h-[730px] md:w-[1450px] lg:-right-[759px] lg:top-[9760px] lg:h-[710px] lg:w-[1441px]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />

        {/* FAQ section lights */}
        {/* Ellipse 1809 - Left (duplicate ellipse name) */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: faqLeftX,
          }}
          className='absolute -left-[260px] top-[12500px] z-[-10] h-[775px] w-[687px] md:-left-[270px] md:top-[12550px] md:h-[790px] md:w-[690px] lg:-left-[280px] lg:top-[12596px] lg:h-[775px] lg:w-[687px] 4xl:left-1/2 4xl:-translate-x-[calc(50%+280px)]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        {/* Ellipse 1808 - Right */}
        <motion.div
          style={{
            background: 'linear-gradient(86deg, #000 9.38%, #71A5FF 58.86%, #000 100%)',
            filter: 'blur(150px)',
            x: faqRightX,
          }}
          className='absolute -right-[280px] top-[12580px] z-[-10] h-[912px] w-[518px] md:-right-[300px] md:top-[12620px] md:h-[930px] md:w-[530px] lg:-right-[326px] lg:top-[12660px] lg:h-[912px] lg:w-[518px] 4xl:left-1/2 4xl:translate-x-[calc(50%-326px)]'
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.1 }}
          variants={gradientVariants}
        />
        <div className='container mx-auto px-4 xs:px-8 lg:grid lg:grid-cols-24 lg:gap-0 xl:px-0'>
          <div className='hidden lg:col-span-2 lg:block'></div>
          <div className='relative mx-auto w-full px-4 sm:px-8 lg:col-span-20 xl:px-0'>
            {/* Hero Section */}
            <section id='hero-section' className='relative z-10 pt-[150px]'>
              <div className='mx-auto max-w-7xl'>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className='bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-center font-heading text-[40px] font-medium leading-[38px] text-transparent sm:text-[56px] sm:leading-[52px] md:text-[64px] md:leading-[60px] lg:text-[72px] lg:leading-[68px] xl:text-[76px] xl:leading-[71px] 2xl:text-[84px] 2xl:leading-[79px] 3xl:text-[96px] 3xl:leading-[90px]'
                >
                  Gleec Exchange Wallet
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className='mx-auto mt-8 max-w-4xl text-center text-[16px] leading-[22px] text-white md:text-[20px] md:leading-[28px] lg:text-[24px] lg:leading-[33px]'
                >
                  Secure, non-custodial, and open source multi-chain wallet. Supports 500+
                  cryptocurrencies across 10+ protocols with a built-in decentralized exchange.
                  Trade and bridge across chains—all powered by Gleec Exchange Wallet&apos;s <br />
                  <Link
                    className='nav-highlight-gradient font-bold'
                    href='/docs/start-here/core-technology-discussions/komodo-defi-framework/#atomic-swaps'
                  >
                    Atomic Swap technology
                  </Link>
                </motion.p>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className='mx-auto mt-6 max-w-3xl text-center text-[16px] leading-[22px] text-white md:text-[20px] md:leading-[28px] lg:text-[24px] lg:leading-[33px]'
                >
                  Transparent, trustless swaps. No intermediaries.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className='mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row'
                >
                  <motion.a
                    href={siteData.webDEXlink}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex h-12 w-48 items-center justify-center rounded-[14px] px-6 py-3 font-heading font-semibold text-white'
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #0891b2 50%, #1DB88C 100%)',
                      boxShadow:
                        '0 0 20px rgba(139, 92, 246, 0.25), 0 0 40px rgba(8, 145, 178, 0.2), 0 0 60px rgba(29, 184, 140, 0.15)',
                      textShadow: '0 2px 10px rgba(0, 0, 0, 0.6), 0 1px 4px rgba(0, 0, 0, 0.4)',
                    }}
                    whileHover={{
                      scale: 1.08,
                      boxShadow:
                        '0 0 30px rgba(139, 92, 246, 0.5), 0 0 60px rgba(8, 145, 178, 0.35), 0 0 80px rgba(29, 184, 140, 0.25)',
                      transition: { duration: 0.3 },
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Launch Wallet
                    <svg
                      className='ml-2'
                      width='14'
                      height='15'
                      viewBox='0 0 14 15'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M6.26172 1.09082C6.45719 1.09089 6.64498 1.16939 6.7832 1.30762C6.92114 1.44576 6.99891 1.63291 6.99902 1.82812C6.99902 2.02352 6.92127 2.21136 6.7832 2.34961C6.64498 2.48783 6.45719 2.56634 6.26172 2.56641H3.89746C3.48053 2.56688 3.08095 2.73253 2.78613 3.02734C2.49132 3.32216 2.32567 3.72174 2.3252 4.13867V10.8662C2.32567 11.2831 2.49132 11.6827 2.78613 11.9775C3.08094 12.2723 3.48054 12.438 3.89746 12.4385H10.625C11.0419 12.438 11.4415 12.2724 11.7363 11.9775C12.0311 11.6827 12.1968 11.2831 12.1973 10.8662V8.50195C12.1973 8.30646 12.2758 8.11871 12.4141 7.98047C12.5522 7.84251 12.7393 7.76477 12.9346 7.76465C13.13 7.76465 13.3178 7.84239 13.4561 7.98047C13.5943 8.11871 13.6728 8.30646 13.6729 8.50195V10.8662C13.6719 11.674 13.3505 12.4493 12.7793 13.0205C12.2081 13.5917 11.4328 13.9131 10.625 13.9141H3.89648C3.08886 13.913 2.31426 13.5916 1.74316 13.0205C1.17196 12.4493 0.850563 11.674 0.849609 10.8662V4.1377C0.850686 3.33005 1.17207 2.55547 1.74316 1.98438C2.31426 1.41328 3.08884 1.0919 3.89648 1.09082H6.26172Z'
                        fill='white'
                        stroke='rgba(255, 255, 255, 0.3)'
                        strokeWidth='0.3'
                      />
                      <path
                        d='M12.9229 1.08691C13.0217 1.08689 13.1196 1.10676 13.2109 1.14453C13.3024 1.18238 13.3861 1.23764 13.4561 1.30762C13.526 1.37758 13.5813 1.46131 13.6191 1.55273C13.6569 1.64409 13.6768 1.74195 13.6768 1.84082V5.4873C13.6768 5.6828 13.5991 5.87052 13.4609 6.00879C13.3227 6.147 13.1349 6.22453 12.9395 6.22461C12.744 6.22461 12.5562 6.14698 12.418 6.00879C12.2797 5.87051 12.2021 5.68286 12.2021 5.4873V3.60547L7.77832 8.02832C7.64007 8.16638 7.45223 8.24414 7.25684 8.24414C7.06163 8.24403 6.87447 8.16624 6.73633 8.02832C6.59812 7.8901 6.5196 7.70229 6.51953 7.50684C6.51953 7.31129 6.59806 7.12363 6.73633 6.98535L11.1572 2.5625H9.28418C8.87536 2.56227 8.52653 2.22811 8.53418 1.81055C8.53796 1.6175 8.61701 1.43302 8.75488 1.29785C8.89275 1.16271 9.07843 1.08688 9.27148 1.08691H12.9229Z'
                        fill='white'
                        stroke='rgba(255, 255, 255, 0.3)'
                        strokeWidth='0.3'
                      />
                    </svg>
                  </motion.a>
                </motion.div>
              </div>
            </section>

            <section className='pb-16 lg:pb-20'>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6 }}
                className='relative mx-auto mt-20 flex max-w-7xl flex-col items-center justify-center'
              >
                <ExportedImage
                  placeholder='empty'
                  src={desktopMobile}
                  alt='Gleec Exchange Wallet Desktop and Mobile'
                  className='w-full max-w-4xl'
                />
                <motion.p
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.9 }}
                  className='relative mt-8 text-center text-base leading-[18px] text-br-lighter-blue md:text-lg md:leading-[22px]'
                >
                  Trade and manage crypto directly, securely, and effortlessly across multiple
                  chains — all in one secure, non-custodial and open source wallet. Available
                  everywhere—mobile, desktop, and web.
                </motion.p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6 }}
                className='relative mt-20 flex flex-col items-center justify-center'
              >
                <motion.h2 className='mb-2 text-center text-[32px] font-semibold leading-[38px] text-transparent text-white sm:text-[40px] sm:leading-[48px] md:text-[48px] md:leading-[56px] lg:text-[52px] lg:leading-[60px] xl:text-[56px] xl:leading-[64px]'>
                  {stats[0].value} Protocols supported
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className='mb-8 text-center text-base leading-relaxed text-br-lighter-blue md:text-lg'
                >
                  1500+ Coins/Tokens supported
                </motion.p>
                <div className='mx-auto max-w-6xl'>
                  <div className='grid grid-cols-3 gap-6 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8'>
                    {Object.entries(protocolAbbreviations)
                      .filter(([_, abbr]) => !['tenderminttoken', 'slp', 'qtum'].includes(abbr))
                      .map(([protocolName, protocol], index) => {
                        let iconPath = `/images/lp/supported-assets/protocols/icons/${protocol}.svg`;
                        return (
                          <div
                            key={`protocol-${index}-${protocol}`}
                            className='flex flex-col items-center gap-2'
                          >
                            <ExportedImage
                              placeholder='empty'
                              src={iconPath}
                              width={40}
                              height={40}
                              className='h-8 w-auto sm:h-10 md:h-12'
                              onError={(e) => handleIconError(e, 'protocol', protocol)}
                            />
                            <span className='whitespace-nowrap text-xs text-white/80'>
                              {protocolName}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </motion.div>
            </section>

            {/* Native Cross-Chain Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='w-full text-center'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='text-center text-[22px] font-semibold leading-[30px] text-white sm:text-[40px] sm:leading-[56px] md:text-[55px] md:leading-[68px] lg:text-[42px] lg:leading-[63px] 3xl:text-[60px] 3xl:leading-[90px]'
                >
                  Native BTC & UTXO ↔ EVM/Cosmos Swaps.
                  <br />
                  No Wrapping, No Middlemen
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='mx-auto mt-8 max-w-5xl text-center text-br-lighter-blue md:text-[20px] md:leading-[28px] lg:text-[24px] lg:leading-[33px]'
                >
                  Gleec Exchange Wallet is the first platform that enables true peer-to-peer,
                  cross-chain swaps — moving seamlessly between native Bitcoin and other UTXO-based
                  coins and the EVM or Cosmos ecosystems — without relying on wrapped tokens,
                  semi-centralized bridges, or CEXs.
                </motion.p>
              </div>
            </section>

            {/* Stats Section - Gleec Exchange Wallet in Numbers */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='mb-16 text-center text-[32px] font-semibold leading-[45px] text-white sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'
                >
                  Gleec Exchange Wallet in Numbers
                </motion.h2>

                {/* First Row - 3 Stats */}
                <div className='grid grid-cols-1 gap-12 md:grid-cols-3'>
                  {stats.slice(0, 3).map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className='text-center'
                    >
                      <div className='mb-4 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-[32px] font-semibold leading-[45px] text-transparent sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                        {stat.value}
                      </div>
                      <div className='mx-auto max-w-xs text-lg text-br-lighter-blue'>
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Second Row - Stat, Image, Stat */}
                <div className='mt-12 grid grid-cols-1 items-center gap-12 md:grid-cols-3'>
                  {/* Stat 4 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    className='text-center'
                  >
                    <div className='mb-4 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-[32px] font-semibold leading-[45px] text-transparent sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                      {stats[3].value}
                    </div>
                    <div className='mx-auto max-w-xs text-lg text-br-lighter-blue'>
                      {stats[3].label}
                    </div>
                  </motion.div>

                  {/* Block Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    className='flex justify-center -md:hidden'
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={block}
                      alt='Blockchain block'
                      className='w-full max-w-[280px]'
                    />
                  </motion.div>

                  {/* Stat 5 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    viewport={{ once: true }}
                    className='text-center'
                  >
                    <div className='mb-4 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-[32px] font-semibold leading-[45px] text-transparent sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                      {stats[4].value}
                    </div>
                    <div className='mx-auto max-w-xs text-lg text-br-lighter-blue'>
                      {stats[4].label}
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Pioneering Tech Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='mb-12 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-center text-[40px] font-medium text-transparent sm:text-[56px] md:text-[64px] lg:text-[72px] xl:text-[76px] 2xl:text-[84px] 3xl:text-[96px]'
                >
                  The Pioneering Tech
                </motion.h2>

                <div className='flex flex-col space-y-8'>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='flex flex-col space-y-8'
                  >
                    <h3 className='mb-6 font-heading text-2xl font-bold text-white md:text-3xl lg:text-6xl'>
                      What is an Atomic Swap?
                    </h3>
                    <ExportedImage
                      placeholder='empty'
                      src={atomicSwapExplained}
                      alt='Atomic Swap Technology'
                      className='w-full rounded-2xl'
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='mx-auto mt-8 flex max-w-3xl flex-col items-center justify-center space-y-4'
                  >
                    <p className='text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      Atomic Swaps are a trustless way to exchange one cryptocurrency for
                      another—directly between users—without needing intermediaries or custodians.
                    </p>
                    <p className='text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      Atomic Swaps use smart contract logic (HTLCs, or hashed timelock contracts for
                      UTXO chains like Bitcoin) to ensure that either both parties of the trade get
                      the opposite party&apos;s assets, or assets return to their original owner.
                    </p>
                    <p className='text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      This makes atomic swapping ideal for exchanging assets between blockchains
                      like Bitcoin and Ethereum. Atomic cross-chain swaps offer a secure and direct
                      method for trading across different networks.
                    </p>
                    <Link
                      href='https://komodoplatform.com/en/academy/atomic-swaps-implementation/'
                      className='mt-6 inline-flex items-center gap-2 text-br-skyblue transition-colors hover:text-white'
                    >
                      Read More
                      <ExportedImage
                        src={gradientLinkArrow}
                        alt='arrow'
                        width={12}
                        height={12}
                        placeholder='empty'
                      />
                    </Link>
                  </motion.div>
                </div>

                {/* Timeline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='mx-auto mt-20 flex flex-col items-center justify-center space-y-6'
                >
                  <h3 className='text-center font-heading text-2xl font-bold text-white md:text-3xl lg:text-6xl'>
                    The Timeline
                  </h3>

                  <Link
                    href='https://komodoplatform.com/en/blog/komodo-and-the-early-pioneers-of-atomic-swaps/'
                    className='mx-auto mt-6 inline-flex items-center gap-2 text-center text-br-skyblue transition-colors hover:text-white'
                  >
                    Read More
                    <ExportedImage
                      src={gradientLinkArrow}
                      alt='arrow'
                      width={12}
                      height={12}
                      placeholder='empty'
                    />
                  </Link>

                  <div className='relative grid grid-cols-1 gap-8 lg:grid-cols-[2fr_2fr] lg:gap-12'>
                    {/* Left: Image (hidden on mobile) */}
                    <div className='hidden flex-col items-center justify-center lg:flex'>
                      <ExportedImage
                        placeholder='empty'
                        src={guiInterface}
                        alt='Blockchain blocks'
                        className='w-full rounded-2xl'
                      />
                    </div>

                    {/* Right: Line + Dots + Text combined */}
                    <div className='relative'>
                      {/* Vertical line */}
                      <div className='absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-br-blue via-br-dark-purple to-br-skyblue' />

                      {/* Timeline items */}
                      <div className='space-y-16'>
                        {timeline.map((item, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className='relative flex items-start gap-8'
                          >
                            {/* Dot on the line */}
                            <div className='absolute -left-2.5 top-2'>
                              <div className='h-5 w-5 rounded-full bg-br-skyblue shadow-lg shadow-br-skyblue/50' />
                            </div>

                            {/* Text */}
                            <div className='ml-6 lg:ml-8'>
                              <div className='font-heading text-2xl font-bold text-white lg:text-3xl'>
                                {item.year}
                              </div>
                              <div className='mt-2 font-heading text-xl text-white lg:text-2xl'>
                                {item.text}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Core Features Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='mb-16 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-center text-[40px] font-medium leading-[38px] text-transparent sm:text-[56px] sm:leading-[52px] md:text-[64px] md:leading-[60px] lg:text-[72px] lg:leading-[68px] xl:text-[76px] xl:leading-[71px] 2xl:text-[84px] 2xl:leading-[79px] 3xl:text-[96px] 3xl:leading-[90px]'
                >
                  Core Features
                </motion.h2>

                <div className='grid gap-12 lg:grid-cols-2'>
                  {/* GUI Wallet Screenshot */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='order-2 mx-auto w-1/2 lg:order-1'
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={noLiquidityPools}
                      alt='No Liquidity Pools'
                      className='h-auto w-full shadow-2xl'
                    />
                  </motion.div>

                  {/* No Slippage Feature */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='order-1 lg:order-2'
                  >
                    <h3 className='mb-6 text-[32px] font-semibold leading-[45px] text-white sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                      No Slippage. No Impermanent Loss.
                    </h3>
                    <p className='text-base leading-relaxed text-br-lighter-blue'>
                      Unlike AMMs that rely on liquidity pools, our Atomic Swaps use a peer-to-peer
                      (P2P) order book model, eliminating the key pain points of DeFi trading:
                    </p>
                    <ul className='mt-4 space-y-3'>
                      <li className='flex items-start'>
                        <span className='mr-2 text-br-skyblue'>✓</span>
                        <span className='text-br-lighter-blue'>
                          <strong>No Impermanent Loss:</strong> Liquidity providers retain full
                          control of their assets. No losses from price divergence.
                        </span>
                      </li>
                      <li className='flex items-start'>
                        <span className='mr-2 text-br-skyblue'>✓</span>
                        <span className='text-br-lighter-blue'>
                          <strong>No Slippage on Large Trades:</strong> Market makers and takers
                          execute trades directly. Pricing is transparent and predictable.
                        </span>
                      </li>
                    </ul>
                  </motion.div>
                </div>

                <div className='mt-12'>
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='mb-8 text-center text-2xl font-bold text-white'
                  >
                    When doing Atomic Swaps
                  </motion.h3>

                  <div className='grid gap-8 md:grid-cols-3'>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.1 }}
                      viewport={{ once: true }}
                      className='text-center'
                    >
                      <div className='mb-4 text-4xl text-br-skyblue'>🔒</div>
                      <h4 className='mb-2 text-lg font-bold text-white'>Secure</h4>
                      <p className='text-sm text-br-lighter-blue'>
                        Smart contracts ensure safe execution
                      </p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      viewport={{ once: true }}
                      className='text-center'
                    >
                      <div className='mb-4 text-4xl text-br-skyblue'>⚡</div>
                      <h4 className='mb-2 text-lg font-bold text-white'>Fast</h4>
                      <p className='text-sm text-br-lighter-blue'>
                        Direct P2P trades without intermediaries
                      </p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      viewport={{ once: true }}
                      className='text-center'
                    >
                      <div className='mb-4 text-4xl text-br-skyblue'>🌐</div>
                      <h4 className='mb-2 text-lg font-bold text-white'>Cross-Chain</h4>
                      <p className='text-sm text-br-lighter-blue'>
                        Trade across different blockchains seamlessly
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </section>

            {/* Security Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <div className='grid gap-12 lg:grid-cols-2'>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                  >
                    <h2 className='mb-6 text-[32px] font-semibold leading-[45px] text-white sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                      Top-Tier Security
                    </h2>
                    <p className='text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      Seed Phrases/Private Keys and all other sensitive data generated by the wallet
                      is managed on your own device and never leaves it. You are always in full
                      control. The DEX is fully decentralized with no middlemen.
                    </p>
                    <p className='mt-4 text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      Gleec Exchange Wallet is based on the most robust and secure technology stack
                      in the industry and underwent thorough security assessments. Validate,
                      don&apos;t trust! Our code and technology is completely{' '}
                      <Link
                        href='https://github.com/GLEECBTC'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='gradient-link-komodo'
                      >
                        open source
                      </Link>{' '}
                      and publicly available.
                    </p>
                    <div className='mt-6 space-y-3'>
                      <div className='flex items-center'>
                        <span className='mr-3 text-br-green'>✓</span>
                        <span className='text-br-lighter-blue'>Non-custodial wallet</span>
                      </div>
                      <div className='flex items-center'>
                        <span className='mr-3 text-br-green'>✓</span>
                        <span className='text-br-lighter-blue'>
                          Private keys never leave your device
                        </span>
                      </div>
                      <div className='flex items-center'>
                        <span className='mr-3 text-br-green'>✓</span>
                        <span className='text-br-lighter-blue'>100% open source code</span>
                      </div>
                      <div className='flex items-center'>
                        <span className='mr-3 text-br-green'>✓</span>
                        <span className='text-br-lighter-blue'>Security audited</span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='flex items-center justify-center'
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={securityVisual}
                      alt='Security Features'
                      className='w-2/3'
                    />
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Asset Support Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <div className='grid gap-12 lg:grid-cols-2'>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='flex items-center justify-center'
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={assetSupportWide}
                      alt={`${miscStats.supportedCryptosCount} Supported Cryptocurrencies`}
                      className='w-full rounded-2xl'
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className='order-1 lg:order-2'
                  >
                    <h2 className='mb-6 text-[32px] font-semibold leading-[45px] text-white sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                      Unmatched Asset Support
                    </h2>
                    <p className='text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      Gleec Exchange Wallet supports native assets across 10+ blockchain protocols.
                      With built-in atomic swaps, users can securely store, manage, and trade coins
                      across these networks - all in a single, non-custodial wallet.
                    </p>
                    <p className='mt-4 text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                      The wallet&apos;s cross-chain swap functionality gives users more freedom and
                      flexibility than any centralized platform could offer.
                    </p>
                    {/* <div className='grid grid-cols-2 gap-4 mt-6'>
                    <div className='p-4 rounded-2xl bg-br-dark-blue'>
                      <div className='text-2xl font-bold text-br-skyblue'>
                        {miscStats.supportedCryptosCount}
                      </div>
                      <div className='text-sm text-br-lighter-blue'>Supported Assets</div>
                    </div>
                    <div className='p-4 rounded-2xl bg-br-dark-blue'>
                      <div className='text-2xl font-bold text-br-skyblue'>{stats[0].value}</div>
                      <div className='text-sm text-br-lighter-blue'>Blockchain Protocols</div>
                    </div>
                  </div> */}

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      viewport={{ once: true }}
                      className='mt-8'
                    >
                      <div className='overflow-x-auto rounded-2xl bg-br-dark-blue p-6'>
                        <table className='w-full text-left'>
                          <thead>
                            <tr className='border-b border-br-check-border'>
                              <th className='pb-3 pr-4 font-heading text-sm font-semibold text-white md:text-base'>
                                Protocol Type
                              </th>
                              <th className='pb-3 pr-4 font-heading text-sm font-semibold text-white md:text-base'>
                                Supported Blockchains
                              </th>
                              <th className='pb-3 font-heading text-sm font-semibold text-white md:text-base'>
                                Examples
                              </th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-br-check-border'>
                            <tr>
                              <td className='py-3 pr-4 text-sm font-medium text-white md:text-base'>
                                UTXO-based
                              </td>
                              <td className='py-3 pr-4 text-sm text-br-lighter-blue md:text-base'>
                                Bitcoin, Litecoin, Dogecoin, Digibyte, KMD, Zcash
                              </td>
                              <td className='py-3 text-sm text-br-lighter-blue md:text-base'>
                                BTC, LTC, DOGE, DGB, KMD, ZEC
                              </td>
                            </tr>
                            <tr>
                              <td className='py-3 pr-4 text-sm font-medium text-white md:text-base'>
                                EVM-based
                              </td>
                              <td className='py-3 pr-4 text-sm text-br-lighter-blue md:text-base'>
                                Ethereum, Binance Smart Chain, Polygon, Avalanche, Fantom
                              </td>
                              <td className='py-3 text-sm text-br-lighter-blue md:text-base'>
                                ETH, BNB, MATIC, AVAX, FTM
                              </td>
                            </tr>
                            <tr>
                              <td className='py-3 pr-4 text-sm font-medium text-white md:text-base'>
                                Others
                              </td>
                              <td className='py-3 pr-4 text-sm text-br-lighter-blue/60 md:text-base'>
                                <span className='italic'>Coming soon: Solana, Tron, etc.</span>
                              </td>
                              <td className='py-3 text-sm text-br-lighter-blue/60 md:text-base'>
                                <span className='italic'>SOL, TRX, etc.</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Add Any EVM Token Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='text-center'
                >
                  <h2 className='mb-6 text-[32px] font-semibold leading-[45px] text-white sm:text-[40px] sm:leading-[56px] md:text-[48px] md:leading-[68px] lg:text-[52px] lg:leading-[73px] xl:text-[54px] xl:leading-[76px] 2xl:text-[58px] 2xl:leading-[82px] 3xl:text-[64px] 3xl:leading-[90px]'>
                    Add Any EVM Token in Seconds
                  </h2>
                  <p className='mx-auto max-w-4xl text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                    Easily expand your wallet&apos;s token list: <br className='-md:hidden' />
                    Just paste any ERC-20, BEP-20, or PLG-20 contract address.{' '}
                    <br className='-md:hidden' />
                    Token details are auto-fetched and added to your wallet UI.
                    <br className='-md:hidden' /> No app update or developer whitelist required.
                  </p>
                  <p className='mx-auto mt-6 max-w-4xl text-base leading-relaxed text-br-lighter-blue md:text-lg'>
                    This smooth integration supports flexible atomic cross-chain swaps with a wide
                    range of assets.
                  </p>
                </motion.div>
              </div>
            </section>

            {/* Comparison Table Section */}
            <section className='relative z-10 py-16 lg:py-20'>
              <div className='mx-auto max-w-7xl'>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='mb-16 bg-gradient-to-r from-[#b8b0ff] to-[#ffffff] bg-clip-text text-center text-[40px] font-medium leading-[38px] text-transparent sm:text-[56px] sm:leading-[52px] md:text-[64px] md:leading-[60px] lg:text-[72px] lg:leading-[68px] xl:text-[76px] xl:leading-[71px] 2xl:text-[84px] 2xl:leading-[79px] 3xl:text-[96px] 3xl:leading-[90px]'
                >
                  The Ultimate Comparison
                </motion.h2>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className='mb-12 w-full'
                >
                  <h3 className='mb-6 text-center text-xl font-semibold text-white sm:text-2xl md:text-3xl lg:text-left lg:text-4xl'>
                    Asset Support
                  </h3>
                  <div className='mx-auto w-full max-w-[95vw]'>
                    <div className='overflow-x-auto rounded-lg shadow-lg'>
                      <div className='inline-block min-w-full align-middle'>
                        <table className='min-w-full border-collapse bg-[#061621]'>
                          <thead>
                            <tr className='border-b border-[#919ad5]'>
                              <th className='p-2 text-left text-xs font-medium text-[#8B8B8B] sm:text-sm md:p-3 md:text-base'>
                                Feature
                              </th>
                              <th className='border-l-[4px] border-r-[4px] border-t-[4px] border-l-[#06b6d4] border-r-[#06b6d4] border-t-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-bold text-[#06b6d4] shadow-[0_0_20px_rgba(6,182,212,0.3)] sm:text-sm md:p-3 md:text-base'>
                                Gleec Exchange Wallet
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                MetaMask
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Trust Wallet
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Uniswap (via Wallet)
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Coinbase Wallet
                              </th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-[#919ad5] border-b border-r border-[#919ad5]'>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                UTXO Support
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <span className='text-base text-yellow-200 sm:text-lg'>⚠</span>
                                  <span className='text-xs text-yellow-200 sm:text-sm md:text-base'>
                                    Partial (view only)
                                  </span>
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                EVM Token Support
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <GreenCheckmark className='h-4 w-4 shrink-0 sm:h-5 sm:w-5' />
                                  <span className='text-xs text-br-lighter-blue sm:text-sm md:text-base'>
                                    Yes (ETH-only)
                                  </span>
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Custom Token Additions
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <GreenCheckmark className='h-5 w-5 shrink-0 sm:h-6 sm:w-6' />
                                  <span className='text-xs text-white sm:text-sm md:text-base'>
                                    InstaList (ERC/BEP/AVX)
                                  </span>
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <GreenCheckmark className='h-4 w-4 shrink-0 sm:h-5 sm:w-5' />
                                  <span className='text-xs text-br-lighter-blue sm:text-sm md:text-base'>
                                    Manual (ERC-20 only)
                                  </span>
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <GreenCheckmark className='h-4 w-4 shrink-0 sm:h-5 sm:w-5' />
                                  <span className='text-xs text-br-lighter-blue sm:text-sm md:text-base'>
                                    Manual
                                  </span>
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center gap-2'>
                                  <span className='text-base text-yellow-200 sm:text-lg'>⚠</span>
                                  <span className='text-xs text-yellow-200 sm:text-sm md:text-base'>
                                    Limited
                                  </span>
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Bridge-Free Native BTC Trading
                              </td>
                              <td className='border-b-[4px] border-l-[4px] border-r-[4px] border-b-[#06b6d4] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <GreenCheckmark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                              <td className='p-2 text-center'>
                                <span className='inline-flex items-center justify-center'>
                                  <RedXMark />
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className='w-full pt-[25px]'
                >
                  <h3 className='mb-6 text-center text-xl font-semibold text-white sm:text-2xl md:text-3xl lg:text-left lg:text-4xl'>
                    Wallet Features
                  </h3>
                  <WalletComparison />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className='mt-12 w-full'
                >
                  <h3 className='mb-6 text-center text-xl font-semibold text-white sm:text-2xl md:text-3xl lg:text-left lg:text-4xl'>
                    DEX Features
                  </h3>
                  <div className='mx-auto w-full max-w-[95vw]'>
                    <div className='overflow-x-auto rounded-lg shadow-lg'>
                      <div className='inline-block min-w-full align-middle'>
                        <table className='min-w-full border-collapse bg-[#061621]'>
                          <thead>
                            <tr className='border-b border-[#919ad5]'>
                              <th className='p-2 text-left text-xs font-medium text-[#8B8B8B] sm:text-sm md:p-3 md:text-base'>
                                Feature
                              </th>
                              <th className='border-l-[4px] border-r-[4px] border-t-[4px] border-l-[#06b6d4] border-r-[#06b6d4] border-t-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-bold text-[#06b6d4] shadow-[0_0_20px_rgba(6,182,212,0.3)] sm:text-sm md:p-3 md:text-base'>
                                Gleec Exchange Wallet Atomic Swaps
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                AMM-Based DEXs (e.g. Uniswap, PancakeSwap)
                              </th>
                              <th className='p-2 text-center text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                CEXs (e.g. Binance, Coinbase)
                              </th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-[#919ad5] border-b border-r border-[#919ad5]'>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Swap Mechanism
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Native cross-chain swaps
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Liquidity pool-based swaps
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Centralized order book matching
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Custody
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Non-custodial
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Non-custodial
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Custodial
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Intermediaries
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                None
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Liquidity Provider smart contracts
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Exchange operators
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Native Asset Support
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Yes (e.g., BTC, LTC, DGB)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                No (only wrapped/native tokens)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Yes
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Security Risk
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Low (hashed timelocks and equivalent)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Medium (smart contract bugs)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Medium (custody + regulatory + hack risks)
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Slippage
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                No (order book)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Yes (depends on liquidity depth)
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                No (limit/market orders)
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Swap Support
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Cross Chain, Cross Platform, UTXO, EVM, Cosmos
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                On a single EVM chain
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Across all Cryptocurrencies they can list
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Trade Finality
                              </td>
                              <td className='border-l-[4px] border-r-[4px] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Deterministic
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Immediate but volatile
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Immediate (centralized systems)
                              </td>
                            </tr>
                            <tr>
                              <td className='p-2 text-xs font-medium text-[#289fc6] sm:text-sm md:p-3 md:text-base'>
                                Trust Assumptions
                              </td>
                              <td className='border-b-[4px] border-l-[4px] border-r-[4px] border-b-[#06b6d4] border-l-[#06b6d4] border-r-[#06b6d4] bg-gradient-to-b from-[#0a2540] to-[#051621] p-2 text-center text-xs font-semibold text-white sm:text-sm md:p-3 md:text-base'>
                                Trustless
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Trust in smart contract + LPs
                              </td>
                              <td className='p-2 text-center text-xs text-br-lighter-blue sm:text-sm md:p-3 md:text-base'>
                                Trust in exchange operator
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className='faq-bg mb-4 pb-[38px] pt-[76px] lg:mb-16 lg:pb-[120px] lg:pt-[100px]'>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='pb-[40px]'
              >
                <motion.h2
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className='text-center font-heading text-[36px] font-semibold leading-[44px] tracking-[-0.72px] text-white'
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.p
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='text-gray-white-2 pb-[56px] pt-[20px] text-center font-heading text-[20px] font-normal leading-[30px]'
                >
                  Everything you need to know about Gleec Exchange Wallet
                </motion.p>
                {faqData.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <FAQ
                      faqNo={item.faqNo}
                      faqQstn={item.faqQstn}
                      faqAnswer={item.faqAnswer}
                      className='faq mb-4'
                      className1=''
                      selected={selected}
                      setSelected={setSelected}
                    />
                  </motion.div>
                ))}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className='mx-auto w-[300px] rounded-[16px] bg-[#051019] px-[32px] pb-[40px] pt-[32px] xs:w-[350px] lg:w-[600px] xl:w-[768px]'
              >
                <h3 className='text-center font-heading text-[20px] font-semibold leading-[30px] text-white'>
                  Still have questions?
                </h3>
                <p className='text-gray-white-2 pt-[8px] text-center font-heading text-[18px] font-normal leading-[28px]'>
                  Can&apos;t find the answer you&apos;re looking for? Please chat to our friendly
                  team.
                </p>
                <div className='mx-auto flex justify-center pt-[32px]'>
                  <Link href='/contact/' target='_blank' className=''>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                      className='get-in-touch-btn flex items-center justify-center'
                    >
                      <span className='px-[24px] py-[12px] font-sans text-base font-semibold leading-[20px] text-black'>
                        Get in touch
                      </span>
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            </section>
            <div className='hidden lg:col-span-2 lg:block'></div>
          </div>
        </div>
      </main>
    </>
  );
}
